import Colors from "@constants/Colors";
import { Skeleton, Stack } from "@mui/material";
import Paper from "@mui/material/Paper";
import TableContainer from "@mui/material/TableContainer";
import { DEVICE, useWindowSizeChanged } from "@hooks/DeviceSize";
import NoDataAvailable from "@shared-ui-components/NoDataAvailable";

import { useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import AlertTriangle from "@icons/AlertTriangle";
import { CHECK_TYPE_COLOR } from "@src/pages/Dashboard/Notification/NotificationList/NotificationCard/helper";
import Ellipse from "@src/assets/icons/Ellipse";
import { TABLE_ROW_COUNT } from "@src/constants";
import CustomTableBody from "./CustomTableBody";
import {
  StyledBox,
  StyledButtonBox,
  StyledLink,
  StyledPagination,
  StyledTable,
  AlertTypography,
  StyledCellBox,
  StyledTableRow,
  StyledBodyTableCell,
} from "./style";
import TableHeader from "./TableHeader";
import { CustomDataTableType, RowData, Column } from "../DataTable/types";
import { Flex } from "../Flex";
import ViewAllButton from "../Buttons";

const CustomDataTable = ({
  rowData,
  isLoading,
  sortBy,
  showPagination,
  handlePageChange,
  dyColumns,
  page,
  handleNavigate,
  sortHandler,
}: CustomDataTableType) => {
  const device = useWindowSizeChanged();
  const isDesktop = device === DEVICE.DESKTOP;
  const [scrollIsActive, setIsScrollActive] = useState(false);
  const tableRef = useRef<HTMLTableElement>(null);
  const path = useLocation().pathname;
  const skeletonArray = Array(showPagination ? 15 : 5).fill("");

  const pagination =
    showPagination &&
    (rowData.length >= 15 || rowData.length > 0
      ? Number(rowData[0].total) >= 15
      : false);

  const convertLink = (label: string, row: RowData, column?: Column) => {
    const arr = row?.type?.split(",");
    const linkPath =
      column?.id === "check_no" && row.property_short_name
        ? `${path}/${row.property_short_name}/${label}`
        : `${path}/${label}`;
    return (
      <Flex alignItems="center" gap="4">
        <StyledLink to={linkPath} color={Colors.primary}>
          {label}
        </StyledLink>
        {row.type &&
          arr?.map((type: string) => (
            <Ellipse
              key={type}
              width="9"
              height="9"
              color={CHECK_TYPE_COLOR[type]}
            />
          ))}
      </Flex>
    );
  };

  const addAlert = (label: string) => {
    return (
      <StyledCellBox>
        <AlertTriangle
          width="14"
          height="14"
          color={Colors.error_500}
          strokeWidth="1"
        />
        <AlertTypography>{label}</AlertTypography>
      </StyledCellBox>
    );
  };

  useEffect(() => {
    const handleScroll = () => {
      const { scrollLeft, scrollWidth, clientWidth } =
        tableRef.current as HTMLTableElement;
      const isScrollingHorizontally = scrollLeft < scrollWidth - clientWidth;
      if (isScrollingHorizontally) {
        setIsScrollActive(true);
      }
    };
    if (tableRef?.current !== null) {
      handleScroll();
    }
  });

  let content: React.ReactNode = (
    <StyledBox>
      <Paper sx={{ width: "100%" }}>
        <TableContainer
          ref={tableRef}
          sx={{
            backgroundColor: Colors.white,
            paddingBottom: scrollIsActive ? "16px" : "0px !important",
            "&::-webkit-scrollbar": {
              height: 10,
              backgroundColor: Colors.primarygray_200,
            },
            "&::-webkit-scrollbar-thumb": {
              borderRadius: 6,
              backgroundColor: Colors.primarygray_400,
            },
          }}
        >
          <StyledTable stickyHeader aria-label="sticky table">
            <TableHeader
              dyColumns={dyColumns}
              sortHandler={sortHandler}
              rowData={rowData}
              sortBy={sortBy}
              isDesktop={isDesktop}
            />
            {rowData?.length !== 0 ? (
              <CustomTableBody
                rowData={rowData}
                showPagination={showPagination}
                dyColumns={dyColumns}
                convertLink={convertLink}
                addAlert={addAlert}
                loading={isLoading}
                isDesktop={isDesktop}
              />
            ) : (
              skeletonArray?.map(() => (
                <StyledTableRow key={Math.random() + Math.random()}>
                  {dyColumns.map((el) => (
                    <StyledBodyTableCell
                      key={el.id}
                      align={el.align}
                      style={{
                        minWidth: el.minWidth,
                        position: el.freeze ? "sticky" : "unset",
                        left: el.freeze ? 0 : "auto",
                        backgroundColor: Colors.white,
                        borderRight: isDesktop
                          ? `1px solid ${Colors.neutral_gray_300}`
                          : "",
                      }}
                    >
                      <Skeleton />
                    </StyledBodyTableCell>
                  ))}
                </StyledTableRow>
              ))
            )}
          </StyledTable>
        </TableContainer>
      </Paper>
    </StyledBox>
  );

  if (!isLoading && rowData?.length === 0) content = <NoDataAvailable />;

  return (
    <>
      {content}
      {pagination && (
        <StyledButtonBox pagination={showPagination ? "true" : undefined}>
          <Stack spacing={2}>
            <StyledPagination
              size="large"
              page={page + 1}
              defaultPage={page + 1}
              siblingCount={0}
              count={
                Math.ceil(Number(rowData[0]?.total) / TABLE_ROW_COUNT) || 1
              }
              variant="outlined"
              shape="rounded"
              onChange={handlePageChange}
            />
          </Stack>
        </StyledButtonBox>
      )}
      {!showPagination && (
        <ViewAllButton
          disabled={rowData?.length <= 0}
          onclick={handleNavigate}
        />
      )}
    </>
  );
};

export default CustomDataTable;
